import { useState, PropsWithChildren } from "react";

import {
  Button,
  Checkbox,
  CircularProgress,
  Grid,
  MenuItem,
  Select,
  TextField,
} from "@material-ui/core";

import { QUERIES, MUTATIONS } from "../../../../graphql";
import {
  CreateSystemUserData,
  UpdateSystemUserData,
} from "../../../../graphql/models";

import {
  GqlRecordPicker,
  I18NLanguagePicker,
  PhoneInput,
} from "../../../components";

import useUserFormStyle from "./user_form.style";
import { useMemo } from "react";
import { useSnackbarMutation } from "../../../../hooks";
import { ForceResetUserPasswordButton } from "../../../components/force_reset_user_password_button/force_reset_user_password_button";

export interface UserEditFormProps extends PropsWithChildren<any> {
  value: CreateSystemUserData | UpdateSystemUserData | undefined;
  onValueUpdatedSuccessfully: (v?: CreateSystemUserData) => void;
}

export function UserForm(props: UserEditFormProps) {
  const { value, onValueUpdatedSuccessfully } = props;
  const classes = useUserFormStyle();

  const deleteMutation = MUTATIONS.HIDE_USER;
  const [invokeDeleteMutation, { loading: deleting }] =
    useSnackbarMutation(deleteMutation);

  let mutation = MUTATIONS.CREATE_SYSTEM_USER;
  if (value && (value as UpdateSystemUserData).id) {
    mutation = MUTATIONS.UPDATE_SYSTEM_USER;
  }
  const [invokeCreateOrUpdateUser, { loading, data: createOrUpdateUserData }] =
    useSnackbarMutation(mutation);
  const [
    invokeForcePassword,
    { loading: mutating, data: forcePasswordResult },
  ] = useSnackbarMutation(MUTATIONS.FORCE_PASSWORD);
  const [updated, setUpdated] = useState(false);

  const [showDelete, setShowDelete] = useState(false);
  const [username, setUsername] = useState<string>(value?.username ?? "");
  const [password, setPassword] = useState<string>(value?.password ?? "");
  const [firstName, setFirstName] = useState<string>(value?.firstName ?? "");
  const [lastName, setLastName] = useState<string>(value?.lastName ?? "");
  const [role, setRole] = useState<string>(value?.role ?? "");
  const [phone, setPhone] = useState<string>(value?.phone ?? "");
  const [email, setEmail] = useState<string>(value?.email ?? "");
  const [buildings, setBuildings] = useState(value?.buildings ?? []);
  const [language, setLanguage] = useState(value?.language ?? "it");
  const [newPassword, setNewPassword] = useState<string>("");
  const [isTestAccount, setIsTestAccount] = useState<boolean>(value?.isTestAccount ?? false);

  const isValidPassword = useMemo(() => {
    return newPassword && newPassword.length > 6;
  }, [newPassword]);

  const displayBuildings = useMemo(() => {
    return (
      role === "concierge" ||
      role === "building_manager" ||
      role === "service_provider"
    );
  }, [role]);

  const displayPassword = useMemo(() => {
    return !(value as UpdateSystemUserData)?.id;
  }, [value]);

  const onFormSubmit = (evt) => {
    evt.preventDefault();
    invokeCreateOrUpdateUser({
      variables: {
        systemUserData: {
          id: (value as UpdateSystemUserData).id,
          username,
          password,
          firstName,
          lastName,
          role,
          phone,
          email,
          buildings,
          language,
          isTestAccount,
        } as CreateSystemUserData,
      },
    });
  };

  if (createOrUpdateUserData && !updated) {
    onValueUpdatedSuccessfully(createOrUpdateUserData.createSystemUser);
    setUpdated(true);
  }

  const forcePassword = () => {
    invokeForcePassword({
      variables: {
        targetUserId: (value as UpdateSystemUserData).id,
        newPassword,
      },
    });
  };

  if (forcePasswordResult && !updated) {
    onValueUpdatedSuccessfully(forcePasswordResult);
    setUpdated(true);
  }

  const hasApartments = useMemo(() => {
    if (!value) return false;
    if (!value.buildings) return false;
    const apts = value.buildings.reduce((count, building) => {
      if (!building.apartments) return 0;
      return count + building.apartments.length;
    }, 0);
    console.debug(`hasApartments return ${apts}`);
    return apts > 0;
  }, [value]);

  const onDelete = () => {
    invokeDeleteMutation({
      variables: {
        userId: value && value["id"],
      },
    });
    onValueUpdatedSuccessfully();
  };

  return (
    <>
      {loading && `Loading...`}
      {!createOrUpdateUserData && (
        <form
          className={classes.root}
          onSubmit={onFormSubmit}
          noValidate
          autoComplete="off"
        >
          <Grid container spacing={3}>
            <Grid item sm={4} xs={12}>
              <TextField
                required
                className={classes.textField}
                label="Username"
                variant="outlined"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
            </Grid>
            {displayPassword && (
              <Grid item sm={4} xs={12}>
                <TextField
                  required
                  className={classes.textField}
                  label="Password"
                  variant="outlined"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </Grid>
            )}
            <Grid item sm={4} xs={12}>
              <TextField
                required
                className={classes.textField}
                label="Nome"
                variant="outlined"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
            </Grid>
            <Grid item sm={4} xs={12}>
              <TextField
                required
                className={classes.textField}
                label="Cognome"
                variant="outlined"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
            </Grid>
            <Grid item sm={4} xs={12}>
              <Select
                required
                label="Ruolo"
                variant="outlined"
                className={classes.textField}
                value={role}
                onChange={(e) => setRole(e.target.value as string)}
              >
                <MenuItem value={"system_admin"}>System Admin</MenuItem>
                <MenuItem value={"building_manager"}>
                  Amministratore di condominio
                </MenuItem>
                <MenuItem value={"concierge"}>Concierge</MenuItem>
                <MenuItem value={"user"}>Utente</MenuItem>
                <MenuItem value={"service_provider"}>
                  Fornitore di servizi esterni
                </MenuItem>
              </Select>
            </Grid>
            <Grid item sm={4} xs={12}>
              <TextField
                required
                className={classes.textField}
                label="Email"
                variant="outlined"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </Grid>
            <Grid item sm={4} xs={12}>
              <PhoneInput
                required
                className={classes.textField}
                label="Cellulare (prefisso + numero, es. +39xxxxxx)"
                variant="outlined"
                defaultValue={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
            </Grid>
            <Grid item sm={4}>
              <TextField
                style={{ width: "100%" }}
                label="Forza nuova password"
                variant="outlined"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              ></TextField>
            </Grid>
            <Grid item sm={4} xs={12}>
              Lingua:{" "}
              <I18NLanguagePicker value={language} onChange={setLanguage} />
            </Grid>
            {displayBuildings && (
              <Grid item sm={4} xs={12}>
                <GqlRecordPicker
                  required
                  className={classes.textField}
                  multiple={true}
                  value={buildings}
                  getOptionLabel={(record) => record.name}
                  placeHolder={"Palazzi"}
                  renderOption={(record) => record.name}
                  onValueChange={(value) => setBuildings(value)}
                  query={QUERIES.BUILDINGS("light")}
                  queryResultDataField={"buildings"}
                />
              </Grid>
            )}
            <Grid item sm={6} xs={12}>
              <Checkbox
                checked={isTestAccount}
                onChange={(e, v) => setIsTestAccount(v)}
              />{" "}
              Test account
            </Grid>
            {!hasApartments && (
              <Grid item sm={6} xs={12}>
                <Checkbox
                  checked={showDelete}
                  onChange={(e, v) => setShowDelete(v)}
                />{" "}
                Elimina l'utente
              </Grid>
            )}
            <Grid container spacing={5}>
              <Grid item sm={12} xs={12}>
                <Button
                  className={classes.textField}
                  type="submit"
                  variant="contained"
                  color="primary"
                >
                  {!loading ? "Salva" : <CircularProgress />}
                </Button>
              </Grid>
              {isUpdateSystemUser(value) && (
                <Grid item xs={12} md={3}>
                  <ForceResetUserPasswordButton
                    className={classes.textField}
                    user={value}
                    variant="contained"
                    color="primary"
                  />
                </Grid>
              )}
              {isValidPassword && isUpdateSystemUser(value) && (
                <Grid item md={3}>
                  <Button
                    type="button"
                    variant="contained"
                    color="primary"
                    style={{ width: "100%" }}
                    onClick={forcePassword}
                  >
                    {mutating ? <CircularProgress /> : "Forza la password"}
                  </Button>
                </Grid>
              )}
              {showDelete && (
                <Grid item md={3}>
                  <Button
                    className={classes.textField}
                    type="button"
                    variant="contained"
                    color="primary"
                    onClick={onDelete}
                  >
                    {deleting ? <CircularProgress /> : `Elimina!`}
                  </Button>
                </Grid>
              )}
            </Grid>
          </Grid>
        </form>
      )}
    </>
  );
}

function isUpdateSystemUser(u: any | undefined): u is UpdateSystemUserData {
  return u?.id !== undefined;
}
